import React from 'react';

interface TimelineGridSkeletonProps {
  rows?: number;
  cols?: number;
  scale?: number;
  showHeader?: boolean;
}

// Helper to simulate month grouping for skeleton (e.g., 2 months, each with half the days)
function getMonthGroups(cols: number) {
  // For skeleton, just alternate months every ~cols/2 columns
  const groups: { name: string; span: number }[] = [];
  const half = Math.floor(cols / 2);
  groups.push({ name: 'June 2025', span: half });
  groups.push({ name: 'July 2025', span: cols - half });
  return groups;
}

const TimelineGridSkeleton: React.FC<TimelineGridSkeletonProps> = ({ rows = 10, cols = 20, scale = 1, showHeader = true }) => {
  const boxheight = 60 * scale;
  const boxWidth = 75 * scale;
  const days = Array.from({ length: cols });
  const monthGroups = getMonthGroups(cols);

  // Precompute filler rows so we can also render tasks within them
  const availableHeight = typeof window !== 'undefined' ? window.innerHeight - 140 : 600;
  const emptyRowsNeededCalc = Math.max(0, Math.ceil((availableHeight - rows * boxheight) / boxheight));
  const fillerRowCount = Math.min(emptyRowsNeededCalc, 10);

  const colors = ['#bfdbfe', '#fbcfe8', '#bbf7d0', '#fde68a', '#ddd6fe'];
  const presetTasks = [
    { row: 1, start: 2, span: 4 },
    { row: 2, start: 7, span: 3 },
    { row: 3, start: 9, span: 3 },
    { row: 4, start: 12, span: 5 },
    { row: 5, start: 15, span: 3 },
    { row: 6, start: 0, span: 6 },
    { row: 7, start: 10, span: 4 },
    { row: 8, start: 5, span: 4 },
  ];

  const presetDerived = presetTasks
    .filter(t => t.row < rows && t.start + t.span <= cols)
    .map((t, idx) => ({ ...t, color: colors[idx % colors.length] }));

  // Utility to create a patterned task for a given row index
  const makePatternTask = (rowIndex: number) => {
    // Show on many rows using a simple pattern
    const shouldShow = rowIndex % 2 === 0 || rowIndex % 3 === 0;
    if (!shouldShow) return null;
    const maxStart = Math.max(cols - 6, 0);
    const start = Math.min((rowIndex * 3) % (maxStart === 0 ? 1 : maxStart), Math.max(cols - 3, 0));
    const span = Math.min(3 + (rowIndex % 4), Math.max(cols - start, 1));
    return {
      row: rowIndex,
      start,
      span,
      color: colors[rowIndex % colors.length]
    };
  };

  return (
    <div className="relative flex-1" style={{ height: '100%', width: cols * boxWidth }}>
      {showHeader && (
        <>
          {/* Months header row */}
          <div className="flex bg-white dark:bg-[#1a1f2e] border-b border-gray-300 dark:border-gray-700 sticky top-0 z-30">
            {monthGroups.map((group, idx) => (
              <div
                key={group.name}
                className={
                  `flex items-center justify-center border-b border-r text-gray-700 dark:text-white border-gray-200 dark:border-[#374151] animate-pulse ` +
                  (idx % 2 === 0 ? 'bg-gray-50 dark:bg-[#111928]' : 'bg-gray-100 dark:bg-[#151D2E]')
                }
                style={{ width: group.span * boxWidth, minWidth: group.span * boxWidth, height: 36 }}
              >
                <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            ))}
          </div>
          {/* Days header row */}
          <div className="flex bg-white dark:bg-[#1a1f2e] border-b border-gray-300 dark:border-[#2d3748] sticky top-[36px] z-20">
            {days.map((_, index) => (
              <div
                key={`day-${index}`}
                className="flex flex-col items-center justify-center flex-1 min-w-[50px] px-2 py-2 text-center border-b border-gray-300 dark:border-[#2d3748] border-r border-r-gray-300 dark:border-r-[#2d3748]"
                style={{ minWidth: boxWidth, height: 45 }}
              >
                <div className="animate-pulse">
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-5 mx-auto"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-10 mt-1 mx-auto"></div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {/* Grid rows */}
      <div className="relative" style={{ height: rows * boxheight }}>
        {Array.from({ length: rows }).map((_, rowIdx) => {
          const preset = presetDerived.find(t => t.row === rowIdx);
          const pattern = preset ? null : makePatternTask(rowIdx);
          const task = preset || pattern;
          return (
            <div
              key={rowIdx}
              className="relative flex overflow-x-clip bg-white dark:bg-tertiary-dark border-b border-gray-200 dark:border-gray-600/10"
              style={{ height: boxheight }}
            >
              {days.map((_, colIdx) => (
                <div
                  key={colIdx}
                  className="flex-1 border-r border-gray-200 dark:border-gray-500/10 border-l-[0.5px]"
                  style={{ minWidth: boxWidth, maxWidth: boxWidth, height: boxheight }}
                />
              ))}
              {task && (
                <div
                  className="absolute rounded-md p-2 flex flex-col justify-between"
                  style={{
                    left: task.start * boxWidth + 2,
                    top: 2,
                    width: task.span * boxWidth - 4,
                    height: boxheight - 4,
                    backgroundColor: task.color,
                  }}
                >
                  <div className="absolute inset-0 bg-black opacity-0 dark:opacity-70 rounded-md"></div>
                  {/* Skeleton for task title */}
                  <div className="h-4 bg-white/40 rounded w-3/4 animate-pulse relative"></div>
                  
                  {/* Skeleton for duration and completion */}
                  <div className="flex justify-between items-center relative">
                    <div className="h-3 bg-white/40 rounded w-8 animate-pulse"></div>
                    <div className="h-4 bg-white/40 rounded-md w-10 animate-pulse"></div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Render empty rows to fill viewport, similar to TimelineGrid */}
      {Array.from({ length: fillerRowCount }).map((_, emptyIndex) => {
        const rowIndex = rows + emptyIndex;
        const task = makePatternTask(rowIndex);
        return (
          <div
            key={`empty-grid-${emptyIndex}`}
            className="relative flex overflow-x-clip bg-white dark:bg-tertiary-dark border-b border-gray-200 dark:border-gray-600/10"
            style={{ height: boxheight }}
          >
            {days.map((_, colIdx) => (
              <div
                key={`empty-cell-${emptyIndex}-${colIdx}`}
                className="flex-1 border-r border-gray-200 dark:border-gray-500/10 border-l-[0.5px]"
                style={{ minWidth: boxWidth, maxWidth: boxWidth, height: boxheight }}
              />
            ))}
            {task && (
              <div
                className="absolute rounded-md p-2 flex flex-col justify-between"
                style={{
                  left: task.start * boxWidth + 2,
                  top: 2,
                  width: task.span * boxWidth - 4,
                  height: boxheight - 4,
                  backgroundColor: task.color,
                }}
              >
                <div className="absolute inset-0 bg-black opacity-0 dark:opacity-70 rounded-md"></div>
                <div className="h-4 bg-white/40 rounded w-3/4 animate-pulse relative"></div>
                <div className="flex justify-between items-center relative">
                  <div className="h-3 bg-white/40 rounded w-8 animate-pulse"></div>
                  <div className="h-4 bg-white/40 rounded-md w-10 animate-pulse"></div>
                </div>
              </div>
            )}
          </div>
        );
      })}

       {/* Loading indicator (single instance) */}
       <div className="fixed bottom-4 left-[60%] transform -translate-x-1/2 z-50 pointer-events-none">
        <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="w-4 h-4 border-2 border-blue-500 dark:border-blue-400 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm font-medium">Loading timeline...</span>
        </div>
      </div>
    </div>
  );
};

export default TimelineGridSkeleton; 